import { DeliveredCouponStatRow } from '@shared/api/giftCoupon/giftCoupon.controller';
import {
  DELIVERY_CHANNEL_LIST,
  DeliveryChannel,
} from '@shared/api/giftCoupon/giftCoupon.interface';

import { couponEstimateKey } from '../../utils/chargeEstimate.utils';

// ─── 타입 ────────────────────────────────────────────

export type StatsPeriodUnit = 'week' | 'month';

/** 매체 미기재(레거시 발송 건) 버킷 키 */
export const STATS_CHANNEL_UNSPECIFIED = 'unspecified' as const;
export type StatsChannelKey =
  | DeliveryChannel
  | typeof STATS_CHANNEL_UNSPECIFIED;

export interface ChannelAgg {
  count: number;
  amount: number; // 도매가(gifticon.priceWhole) 합
}

export interface DeliveryPeriodRow {
  key: string; // 정렬용 (주: 시작일 ISO date, 월: YYYY-MM)
  label: string;
  startMs: number;
  byChannel: Partial<Record<StatsChannelKey, ChannelAgg>>;
  total: ChannelAgg;
}

export interface DeliveryProductRow {
  key: string; // couponEstimateKey (unit:/info:/item:)
  name: string;
  byChannel: Partial<Record<StatsChannelKey, ChannelAgg>>;
  total: ChannelAgg;
}

export interface DeliveryStats {
  totalCount: number;
  totalAmount: number;
  /** 데이터에 등장한 매체만, DELIVERY_CHANNEL_LIST 순서 + 미기재 마지막 */
  channelsPresent: StatsChannelKey[];
  channelTotals: Partial<Record<StatsChannelKey, ChannelAgg>>;
  /** 최근 기간부터 (startMs 내림차순) */
  periods: DeliveryPeriodRow[];
  /** 총액 내림차순 */
  products: DeliveryProductRow[];
}

// ─── 헬퍼 ────────────────────────────────────────────

/** 월요일 시작 주 (로컬 = KST 기준) */
function weekStartOf(d: Date): Date {
  const dayFromMonday = (d.getDay() + 6) % 7;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - dayFromMonday);
}

function toKeyDate(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function periodOf(deliveredAt: Date, unit: StatsPeriodUnit) {
  if (unit === 'month') {
    const start = new Date(deliveredAt.getFullYear(), deliveredAt.getMonth(), 1);
    return {
      key: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}`,
      label: `${start.getFullYear()}년 ${start.getMonth() + 1}월`,
      startMs: start.getTime(),
    };
  }
  const start = weekStartOf(deliveredAt);
  const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
  return {
    key: toKeyDate(start),
    label: `${start.getMonth() + 1}/${start.getDate()}~${
      end.getMonth() + 1
    }/${end.getDate()}`,
    startMs: start.getTime(),
  };
}

function rowChannel(row: DeliveredCouponStatRow): StatsChannelKey {
  return row.gifticon?.deliveryChannel ?? STATS_CHANNEL_UNSPECIFIED;
}

function rowProductName(row: DeliveredCouponStatRow): string {
  const item = typeof row.giftItem === 'object' ? row.giftItem : null;
  const info =
    item && typeof item.giftItemInfo === 'object' ? item.giftItemInfo : null;
  const base = item?.name || info?.name || '(상품 정보 없음)';
  const unitName =
    row.selectedChoiceUnit && typeof row.selectedChoiceUnit === 'object'
      ? row.selectedChoiceUnit.name
      : undefined;
  return unitName ? `${base} - ${unitName}` : base;
}

function addTo(
  byChannel: Partial<Record<StatsChannelKey, ChannelAgg>>,
  channel: StatsChannelKey,
  amount: number,
) {
  const agg = byChannel[channel] ?? { count: 0, amount: 0 };
  agg.count += 1;
  agg.amount += amount;
  byChannel[channel] = agg;
}

// ─── 빌더 ────────────────────────────────────────────

export function buildDeliveryStats(
  rows: DeliveredCouponStatRow[],
  unit: StatsPeriodUnit,
): DeliveryStats {
  const channelTotals: Partial<Record<StatsChannelKey, ChannelAgg>> = {};
  const periodMap = new Map<string, DeliveryPeriodRow>();
  const productMap = new Map<string, DeliveryProductRow>();
  let totalCount = 0;
  let totalAmount = 0;

  for (const row of rows) {
    const deliveredAt = row.gifticon?.deliveredAt
      ? new Date(row.gifticon.deliveredAt)
      : null;
    if (!deliveredAt || isNaN(deliveredAt.getTime())) continue;

    const channel = rowChannel(row);
    const amount = row.gifticon?.priceWhole ?? 0;

    totalCount += 1;
    totalAmount += amount;
    addTo(channelTotals, channel, amount);

    const p = periodOf(deliveredAt, unit);
    const period = periodMap.get(p.key) ?? {
      ...p,
      byChannel: {},
      total: { count: 0, amount: 0 },
    };
    addTo(period.byChannel, channel, amount);
    period.total.count += 1;
    period.total.amount += amount;
    periodMap.set(p.key, period);

    const productKey = couponEstimateKey(row);
    const product = productMap.get(productKey) ?? {
      key: productKey,
      name: rowProductName(row),
      byChannel: {},
      total: { count: 0, amount: 0 },
    };
    addTo(product.byChannel, channel, amount);
    product.total.count += 1;
    product.total.amount += amount;
    productMap.set(productKey, product);
  }

  const channelsPresent: StatsChannelKey[] = [
    ...DELIVERY_CHANNEL_LIST.filter((ch) => channelTotals[ch]),
    ...(channelTotals[STATS_CHANNEL_UNSPECIFIED]
      ? [STATS_CHANNEL_UNSPECIFIED]
      : []),
  ];

  return {
    totalCount,
    totalAmount,
    channelsPresent,
    channelTotals,
    periods: Array.from(periodMap.values()).sort((a, b) => b.startMs - a.startMs),
    products: Array.from(productMap.values()).sort(
      (a, b) => b.total.amount - a.total.amount,
    ),
  };
}
